<script setup lang="ts">
const { open: openIntentModal } = useIntentModal()

const scrollToSection = (id: string) => {
  const element = document.getElementById(id);
  if (element) {
    element.scrollIntoView({ behavior: "smooth" });
  }
};
</script>

<template>
  <section class="relative min-h-150 overflow-hidden lg:min-h-175">
    <!-- Background with image and gradient overlay -->
    <div class="absolute inset-0 z-0">
      <ResponsiveImage
        src="/home-hero.webp"
        alt="Network engineer with a laptop checking server racks in a data centre"
        class="h-full w-full object-cover object-right"
        :width="1920"
        :height="1080"
      />
      <!-- Left-to-right gradient to reveal image on right -->
      <div
        class="absolute inset-0 bg-linear-to-r from-[#0F172A] via-[#0F172A]/90 to-[#0F172A]/35"
      ></div>
      <!-- Mobile: darker overlay for readability -->
      <div class="absolute inset-0 bg-[#0F172A]/60 lg:hidden"></div>
      <!-- Subtle green accent glow -->
      <div
        class="absolute -left-20 top-1/3 h-96 w-96 rounded-full bg-primary-400/20 blur-3xl mix-blend-screen"
      ></div>
    </div>

    <!-- Content -->
    <div
      class="relative z-10 mx-auto flex min-h-150 max-w-7xl flex-col justify-center px-6 py-20 lg:min-h-175 lg:px-8"
    >
      <div
        class="flex max-w-2xl flex-col items-center text-center lg:items-start lg:text-left"
      >
        <!-- Trust badge -->
        <div class="mb-6">
          <span
            class="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-4 py-1.5 text-sm font-medium text-white backdrop-blur-md"
          >
            <span class="relative flex h-2 w-2">
              <span
                class="absolute inline-flex h-full w-full rounded-full bg-primary-400 opacity-75"
              ></span>
              <span
                class="relative inline-flex h-2 w-2 rounded-full bg-primary-500"
              ></span>
            </span>
            Supporting 100+ sites across Australia
          </span>
        </div>

        <!-- Headline -->
        <h1
          class="text-4xl font-bold tracking-tight text-white sm:text-5xl md:text-6xl lg:text-7xl"
        >
          Your Single Partner for
          <span class="text-primary-200"> Security, Connectivity &amp; IT </span>
        </h1>

        <!-- Subheadline -->
        <p class="mt-6 text-lg leading-relaxed text-slate-300 sm:text-xl md:mt-8">
          Integrated solutions for businesses that demand reliability. <br />No more
          juggling vendors or firefighting IT issues.
        </p>

        <!-- CTA Buttons -->
        <div
          class="mt-10 flex w-full flex-col gap-4 sm:flex-row sm:justify-center lg:justify-start"
        >
          <button
            type="button"
            class="inline-flex items-center justify-center rounded-full bg-primary-400 px-8 py-4 text-base font-bold text-white shadow-lg shadow-primary/20 transition-all hover:bg-primary-600 hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-[#0F172A]"
            @click="openIntentModal('free-quote')"
          >
            Get a Free Quote
          </button>
          <button
            type="button"
            class="inline-flex items-center justify-center rounded-full border border-white/30 bg-white/5 px-8 py-4 text-base font-semibold text-white backdrop-blur-sm transition-all hover:bg-white hover:text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#0F172A]"
            @click="openIntentModal('consultation')"
          >
            Book a Consultation
          </button>
        </div>

        <!-- Problem chips -->
        <div class="mt-12 flex flex-wrap justify-center gap-3 lg:justify-start">
          <button
            class="group inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm font-medium text-slate-200 transition-all hover:border-primary/50 hover:bg-primary-400/10 hover:text-white"
            @click="scrollToSection('connectivity')"
          >
            <Icon name="lucide:wifi-off" class="h-4 w-4 text-red-400" />
            Internet keeps dropping
            <Icon
              name="lucide:arrow-right"
              class="h-3 w-3 opacity-50 transition-transform group-hover:translate-y-0.5 group-hover:opacity-100"
            />
          </button>

          <button
            class="group inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm font-medium text-slate-200 transition-all hover:border-primary/50 hover:bg-primary-400/10 hover:text-white"
            @click="scrollToSection('security')"
          >
            <Icon name="lucide:shield-off" class="h-4 w-4 text-orange-400" />
            Security has gaps
            <Icon
              name="lucide:arrow-right"
              class="h-3 w-3 opacity-50 transition-transform group-hover:translate-y-0.5 group-hover:opacity-100"
            />
          </button>

          <button
            class="group inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm font-medium text-slate-200 transition-all hover:border-primary/50 hover:bg-primary-400/10 hover:text-white"
            @click="scrollToSection('itcloud')"
          >
            <Icon name="lucide:flame" class="h-4 w-4 text-yellow-400" />
            Stuck firefighting IT
            <Icon
              name="lucide:arrow-right"
              class="h-3 w-3 opacity-50 transition-transform group-hover:translate-y-0.5 group-hover:opacity-100"
            />
          </button>
        </div>
      </div>
    </div>
  </section>
</template>
